import type { Instrument } from '@amarantos/api-client'
import { getListInstrumentsQueryOptions, useListInstrumentsSuspense } from '@amarantos/api-client'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useCallback, useDeferredValue } from 'react'
import { useTranslation } from 'react-i18next'
import { pick } from 'remeda'
import { ConfirmDeleteDialog } from '@/components/confirm-delete-dialog'
import { DeleteButton } from '@/components/delete-button'
import { PageContent, PageHeader, PageTitle } from '@/components/page'
import { PaginationBar } from '@/components/pagination-bar'
import { RouteErrorFallback } from '@/components/route-error-fallback'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
	InstrumentFormDrawer,
	type InstrumentFormDrawerState,
} from '@/features/instruments/components/instrument-form-drawer'
import { InstrumentsTable } from '@/features/instruments/components/instruments-table'
import { useDeleteInstrumentMutation } from '@/features/instruments/hooks/use-instrument-mutations'
import { instrumentsSearchSchema, toListInstrumentsParams } from '@/features/instruments/utils/search'
import { useConfirmAction } from '@/hooks/use-confirm-action'
import { useDebouncedInput } from '@/hooks/use-debounced-input'
import { getApiErrorMessage } from '@/utils/errors'

export const Route = createFileRoute('/_authenticated/instruments')({
	validateSearch: instrumentsSearchSchema,
	loaderDeps: ({ search }) => search,
	loader: ({ context: { queryClient }, deps }) => {
		queryClient.ensureQueryData(getListInstrumentsQueryOptions(toListInstrumentsParams(deps)))
	},
	errorComponent: ({ error, reset }) => (
		<RouteErrorFallback error={error} reset={reset} fallbackMessageKey='error.load_instruments' />
	),
	component: InstrumentsRoute,
})

function InstrumentsRoute() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const navigate = Route.useNavigate()
	const deferredSearch = useDeferredValue(search)
	const isStale = deferredSearch !== search

	const instrumentsQuery = useListInstrumentsSuspense(toListInstrumentsParams(deferredSearch))

	const deleteInstrument = useConfirmAction<Instrument, { id: Instrument['id'] }>({
		mutation: useDeleteInstrumentMutation(),
		toVariables: (instrument) => ({ id: instrument.id }),
		formatError: (err) => getApiErrorMessage(err, t('error.delete_instrument')),
	})

	const instruments = instrumentsQuery.data?.data ?? []
	const meta = instrumentsQuery.data?.meta
	const currentPage = search.page
	const totalPages = meta?.total_pages ?? 1
	const drawerState: InstrumentFormDrawerState = pick(search, ['mode', 'instrumentId'])
	const drawerOpen = drawerState.mode !== undefined

	const onSearchChange = useCallback(
		(nextValue: string) => {
			navigate({
				replace: true,
				search: (previous) => ({
					...previous,
					search: nextValue,
					page: 1,
				}),
			})
		},
		[navigate],
	)

	const searchInputProps = useDebouncedInput({
		value: search.search,
		onDebouncedChange: onSearchChange,
	})

	return (
		<>
			<PageHeader breadcrumbs={[{ label: t('nav.instruments') }]}>
				<PageTitle
					subtitle={t('instruments.page.description')}
					actions={
						<Button asChild>
							<Link to='.' search={(previous) => ({ ...previous, mode: 'create' as const, instrumentId: undefined })}>
								{t('instruments.create.button')}
							</Link>
						</Button>
					}
				>
					{t('nav.instruments')}
				</PageTitle>
			</PageHeader>
			<PageContent>
				<Card>
					<CardContent className='space-y-4'>
						<Input {...searchInputProps} placeholder={t('instruments.search.placeholder')} className='max-w-sm' />

						<InstrumentsTable
							instruments={instruments}
							isStale={isStale}
							actions={(instrument) => (
								<>
									<Button asChild variant='outline' size='sm'>
										<Link
											to='.'
											search={(previous) => ({
												...previous,
												mode: 'edit' as const,
												instrumentId: instrument.id,
											})}
										>
											{t('instruments.edit.button')}
										</Link>
									</Button>
									<DeleteButton onClick={() => deleteInstrument.request(instrument)} />
								</>
							)}
						/>

						<PaginationBar
							currentPage={currentPage}
							totalPages={totalPages}
							onPageChange={(page) => navigate({ search: (previous) => ({ ...previous, page }) })}
						/>
					</CardContent>
				</Card>

				<InstrumentFormDrawer
					{...drawerState}
					open={drawerOpen}
					onOpenChange={(isOpen) => {
						if (!isOpen) {
							navigate({
								search: (previous) => ({
									...previous,
									mode: undefined,
									instrumentId: undefined,
								}),
							})
						}
					}}
				/>

				<ConfirmDeleteDialog
					{...deleteInstrument.dialogProps}
					title={t('instruments.delete.title')}
					description={t('instruments.delete.description', {
						name: deleteInstrument.target?.serial_number ?? t('instruments.delete.fallback_name'),
					})}
				/>
			</PageContent>
		</>
	)
}
